import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircleHelp, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import SettingRow from '../SettingRow';

interface KeywordMatchingSettingProps {
  enabled: boolean;
  disabled: boolean;
  canManage: boolean;
  onToggle: (next: boolean) => void;
}

const SWITCH_ID = 'admin-setting-keyword-matching';

/**
 * The help trigger sits beside the label rather than inside it. Inside a
 * `<label>` it would either swallow the click (interactive descendants suppress
 * label activation) or, unfocusable as MUI left it, stay mouse-only.
 */
const KeywordMatchingSetting: React.FC<KeywordMatchingSettingProps> = ({
  enabled,
  disabled,
  canManage,
  onToggle,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <SettingRow
        htmlFor={SWITCH_ID}
        disabled={disabled}
        label={t('settings.keywordMatching')}
        description={
          enabled
            ? t('settings.keywordMatchingEnabledDescription')
            : t('settings.keywordMatchingDisabledDescription')
        }
        control={
          <>
            <TooltipIconButton
              label={t('settings.keywordMatchingTooltip')}
              icon={<CircleHelp strokeWidth={1.9} />}
              size="icon-sm"
              className="size-6 text-muted-foreground"
            />
            <Switch
              id={SWITCH_ID}
              checked={enabled}
              disabled={disabled}
              onCheckedChange={onToggle}
            />
          </>
        }
      />

      {!canManage && (
        <Alert variant="info" className="mt-4">
          <Info className="h-4 w-4" />
          <AlertDescription className="revy-cjk leading-[1.6]">
            {t('settings.adminRequired')}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default KeywordMatchingSetting;
